// The pieces both daily games are built from: the page around them, the
// progress strip, the 10 second clock, a team helmet, and the result screen.
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Share } from "lucide-react";
import { topNavStyle } from "../components/nav/topNavStyle";
import { getTeamInitials } from "../utils/teamName";
import { shareText } from "../capacitor/share";
import { SECONDS_PER_QUESTION } from "./daily";

export const CARD = { backgroundColor: "var(--color-bg-card)", border: "1.5px solid var(--color-bg-subtle)", borderRadius: 16, overflow: "hidden" };
export const PRIMARY_BUTTON = {
  display: "block", width: "100%", padding: "14px 0", borderRadius: 50, border: "none",
  backgroundColor: "var(--color-cta)", color: "var(--color-cta-text)", fontSize: 15, fontWeight: 700, cursor: "pointer",
};
export const SECONDARY_BUTTON = { ...PRIMARY_BUTTON, backgroundColor: "transparent", color: "var(--color-cta)", border: "2px solid var(--color-cta)" };

/**
 * The page: a top bar with a way back, and a card with the game's name.
 * Back goes to wherever the player came from (/home, or /demo for a visitor),
 * or /home when the game was opened directly.
 */
export function GamePage({ title, subtitle, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const back = () => (location.key !== "default" ? navigate(-1) : navigate("/home"));

  return (
    <div className="theme-auto" style={{ minHeight: "100vh", backgroundColor: "var(--color-bg-page)", paddingBottom: 48 }}>
      <nav style={topNavStyle({ paddingX: 8, paddingY: 6 })}>
        <button
          type="button"
          onClick={back}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 44, padding: "0 8px", border: "none", background: "none", fontSize: 15, fontWeight: 600, color: "var(--color-cta)", cursor: "pointer" }}
        >
          <ArrowLeft size={18} strokeWidth={2.2} /> Back
        </button>
      </nav>
      <div style={{ maxWidth: 600, margin: "0 auto", padding: "16px 16px 0" }}>
        <div style={CARD}>
          <div style={{ backgroundColor: "var(--color-band-bg)", borderBottom: "1px solid var(--color-band-border)", padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span style={{ fontSize: 16, fontWeight: 900, color: "var(--color-text-strong)", letterSpacing: "0.02em", textTransform: "uppercase" }}>{title}</span>
            {subtitle && <span style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-muted)", whiteSpace: "nowrap" }}>{subtitle}</span>}
          </div>
          <div style={{ padding: 16 }}>{children}</div>
        </div>
      </div>
    </div>
  );
}

/** One bar per question: green right, red wrong, dark for the current one. */
export function Pips({ answers, total, current = null, height = 6, gap = 3 }) {
  return (
    <div style={{ display: "flex", gap, width: "100%" }} aria-hidden="true">
      {Array.from({ length: total }, (_, i) => {
        const color = i < answers.length
          ? (answers[i] ? "var(--color-success)" : "var(--color-danger)")
          : i === current ? "var(--color-text-strong)" : "var(--color-bg-subtle)";
        return <span key={i} style={{ flex: 1, height, borderRadius: height / 2, backgroundColor: color }} />;
      })}
    </div>
  );
}

/** "3 of 10" and the strip beside it. */
export function Progress({ answers, total, current, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: "var(--color-text-strong)", whiteSpace: "nowrap" }}>
        {label || `${Math.min(current + 1, total)} of ${total}`}
      </span>
      <div style={{ flex: 1, maxWidth: 170, marginLeft: "auto" }}><Pips answers={answers} total={total} current={current} /></div>
    </div>
  );
}

/**
 * The 10 second clock for one question. `running` starts it, a new `questionKey`
 * restarts it, and `onExpire` fires once when it reaches zero.
 *
 * It counts against a deadline, not by ticking down, so leaving the app does
 * not pause it: come back after the deadline and the question has already
 * timed out. A paused clock would let anyone switch apps and look the answer
 * up.
 */
export function useQuestionClock({ running, questionKey, onExpire }) {
  const [remaining, setRemaining] = useState(SECONDS_PER_QUESTION * 1000);
  const expire = useRef(onExpire);
  expire.current = onExpire;

  useEffect(() => {
    if (!running) return undefined;
    const deadline = Date.now() + SECONDS_PER_QUESTION * 1000;
    let fired = false;
    const tick = () => {
      const left = Math.max(0, deadline - Date.now());
      setRemaining(left);
      if (left === 0 && !fired) {
        fired = true;
        expire.current();
      }
    };
    tick();
    const id = setInterval(tick, 100);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [running, questionKey]);

  return running ? remaining : SECONDS_PER_QUESTION * 1000;
}

/** The clock as a bar that empties, amber for the last three seconds. */
export function TimerBar({ remaining, stopped = false }) {
  const fraction = stopped ? 0 : remaining / (SECONDS_PER_QUESTION * 1000);
  const low = remaining <= 3000;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ height: 6, borderRadius: 3, backgroundColor: "var(--color-bg-subtle)", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${fraction * 100}%`, borderRadius: 3, backgroundColor: low ? "var(--color-warning)" : "var(--color-success)", transition: "width 100ms linear" }} />
      </div>
      {/* Hidden rather than removed when the clock stops, so the answer
          does not shift the page up under the player's thumb. */}
      <div style={{ visibility: stopped ? "hidden" : "visible", fontSize: 11, fontWeight: 600, color: low ? "var(--color-warning-text)" : "var(--color-text-muted)", textAlign: "right", marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
        {Math.ceil(remaining / 1000)}s
      </div>
    </div>
  );
}

/**
 * A team helmet on a soft circle. The circle is there for the white helmets
 * (Penn State, Ohio State's silver), which vanish on a white card without it.
 */
export function Helmet({ team, size = 64 }) {
  const [failed, setFailed] = useState(false);
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", backgroundColor: "var(--color-bg-subtle)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {failed || !team?.logo ? (
        <span style={{ fontSize: size * 0.3, fontWeight: 800, color: "var(--color-text-muted)" }}>{getTeamInitials(team?.name || "")}</span>
      ) : (
        <img src={team.logo} alt="" onError={() => setFailed(true)} style={{ width: size * 0.8, height: size * 0.8, objectFit: "contain" }} />
      )}
    </div>
  );
}

/** The practice label: one question, no clock, does not count. */
export function PracticeTag() {
  return (
    <span style={{ display: "inline-block", fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", backgroundColor: "var(--color-warning-bg)", color: "var(--color-warning-text)", padding: "3px 8px", borderRadius: 6, marginBottom: 10 }}>
      Practice, doesn't count
    </span>
  );
}

/** A full width Share button. Says so when the text was copied instead. */
export function ShareResult({ getText }) {
  const [note, setNote] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const onClick = async () => {
    const result = await shareText(getText(), { title: "Lineup CFB" });
    if (result === "copied" || result === "failed") {
      setNote(result);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setNote(null), 2500);
    }
  };

  return (
    <button type="button" onClick={onClick} style={{ ...PRIMARY_BUTTON, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
      {note === "copied" ? <Check size={17} /> : <Share size={17} />}
      {note === "copied" ? "Copied to clipboard" : note === "failed" ? "Couldn't share" : "Share your score"}
    </button>
  );
}

/** The end of a game: the score, what will be shared, Share and Done. */
export function GameOver({ answers, total, text, onDone, note, onPlayAgain }) {
  const score = answers.filter(Boolean).length;
  return (
    <div>
      <Pips answers={answers} total={total} />
      <p style={{ textAlign: "center", fontSize: 48, fontWeight: 900, color: "var(--color-text-strong)", margin: "18px 0 0", fontVariantNumeric: "tabular-nums" }}>
        {score}/{total}
      </p>
      <p style={{ textAlign: "center", fontSize: 13, color: "var(--color-text-muted)", margin: "2px 0 16px" }}>
        {note}
      </p>
      <div style={{ backgroundColor: "var(--color-bg-subtle)", borderRadius: 12, padding: "10px 12px", fontSize: 13, lineHeight: 1.55, whiteSpace: "pre-line", color: "var(--color-text-strong)", marginBottom: 12 }}>
        {text}
      </div>
      <ShareResult getText={() => text} />
      {onPlayAgain && <button type="button" onClick={onPlayAgain} style={{ ...SECONDARY_BUTTON, marginTop: 8 }}>Play again</button>}
      <button type="button" onClick={onDone} style={{ ...SECONDARY_BUTTON, marginTop: 8 }}>Done</button>
    </div>
  );
}

export function GameMessage({ children }) {
  return <p style={{ fontSize: 14, color: "var(--color-text-muted)", margin: "8px 0", lineHeight: 1.5 }}>{children}</p>;
}
